import { useState } from "react";
import SiteLayout from "@/components/site/SiteLayout";
import { api } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { BRAND } from "@/config/brand";
import { Mail, Phone, MapPin } from "lucide-react";
import useSeo from "@/lib/useSeo";

export default function Contact() {
  useSeo({
    title: "Contact GC Nexon — Get Help with Your Gift Card Submission",
    description: "Reach the GC Nexon support team for help with your submission, payout, or account. Email, chat, or phone — real humans reply within one business day.",
    path: "/contact",
  });
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent. We'll reply as soon as we can.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch { toast.error("Something went wrong. Try again."); }
    finally { setSending(false); }
  };

  return (
    <SiteLayout>
      <Toaster richColors position="top-center" />
      <section className="section-py container-x">
        <div className="grid lg:grid-cols-2 gap-14">
          <div>
            <span className="inline-block px-4 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-bold uppercase mb-4">Contact</span>
            <h1 className="text-5xl md:text-6xl font-extrabold tracking-tight">Get in touch.</h1>
            <p className="mt-4 text-muted-foreground text-lg">The fastest way to reach us is the chat widget (Miles replies in seconds). Or use the options below.</p>

            <div className="mt-8 space-y-4">
              <div className="card-soft flex items-center gap-4">
                <div className="w-11 h-11 rounded-2xl bg-primary/10 text-primary flex items-center justify-center"><Mail size={20} /></div>
                <div><div className="text-xs uppercase font-bold text-muted-foreground">Email</div><a href={`mailto:${BRAND.email}`} className="font-extrabold hover:text-primary">{BRAND.email}</a></div>
              </div>
              <div className="card-soft flex items-center gap-4">
                <div className="w-11 h-11 rounded-2xl bg-primary/10 text-primary flex items-center justify-center"><Phone size={20} /></div>
                <div><div className="text-xs uppercase font-bold text-muted-foreground">Phone</div><a href={`tel:${BRAND.phone}`} className="font-extrabold hover:text-primary">{BRAND.phone}</a></div>
              </div>
              <div className="card-soft flex items-center gap-4">
                <div className="w-11 h-11 rounded-2xl bg-primary/10 text-primary flex items-center justify-center"><MapPin size={20} /></div>
                <div><div className="text-xs uppercase font-bold text-muted-foreground">Address</div><div className="font-extrabold">{BRAND.address}</div></div>
              </div>
            </div>
          </div>

          <form onSubmit={submit} className="card-soft space-y-4" data-testid="contact-form">
            <h2 className="font-extrabold text-2xl">Send us a message</h2>
            <div>
              <label className="text-xs font-bold uppercase text-muted-foreground">Name</label>
              <input data-testid="contact-name" required className="mt-2 w-full rounded-2xl border border-border bg-white px-4 py-3" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} />
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-muted-foreground">Email</label>
              <input data-testid="contact-email" type="email" required className="mt-2 w-full rounded-2xl border border-border bg-white px-4 py-3" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} />
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-muted-foreground">Subject</label>
              <input data-testid="contact-subject" required className="mt-2 w-full rounded-2xl border border-border bg-white px-4 py-3" value={form.subject} onChange={(e) => setForm({...form, subject: e.target.value})} />
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-muted-foreground">Message</label>
              <textarea data-testid="contact-message" required rows={5} className="mt-2 w-full rounded-2xl border border-border bg-white px-4 py-3" value={form.message} onChange={(e) => setForm({...form, message: e.target.value})} />
            </div>
            <button disabled={sending} data-testid="contact-submit" className="btn-pill w-full">{sending ? "Sending…" : "Send message"}</button>
          </form>
        </div>
      </section>
    </SiteLayout>
  );
}
